/**
 * usePermissions Hook
 * Provides permission checking functionality based on user roles
 */

import { useAuth } from '../context/SimpleAuthContext';
import { UserRole, Permission, hasPermission as checkPermission, hasAnyRolePermission } from '../types/user';

/**
 * Custom hook for checking user permissions
 */
export const usePermissions = () => {
  const { user } = useAuth();
  
  /**
   * Get the user's role as UserRole enum
   */
  const getUserRole = (): UserRole => {
    if (!user?.role) return UserRole.EXTERNAL_CONSULTANT; // Default to most restrictive
    
    // Map the role string to UserRole enum
    const roleMap: Record<string, UserRole> = {
      'Administrator': UserRole.ADMIN,
      'Compliance Officer': UserRole.COMPLIANCE_OFFICER,
      'Risk & Control Specialist': UserRole.RISK_SPECIALIST,
      'Implementation Officer': UserRole.IMPLEMENTATION_OFFICER,
      'Department Manager': UserRole.DEPARTMENT_MANAGER,
      'Task Implementer': UserRole.TASK_IMPLEMENTER,
      'Auditor': UserRole.AUDITOR,
      'Executive': UserRole.EXECUTIVE,
      'Document Manager': UserRole.DOCUMENT_MANAGER,
      'External Consultant': UserRole.EXTERNAL_CONSULTANT
    };
    
    return roleMap[user.role] || UserRole.EXTERNAL_CONSULTANT;
  };
  
  /**
   * Check if the current user has a specific permission
   */
  const hasPermission = (permission: Permission): boolean => {
    const userRole = getUserRole();
    return checkPermission(userRole, permission);
  };
  
  /**
   * Check if the current user has any of the specified roles
   */
  const hasAnyRole = (roles: UserRole[]): boolean => {
    const userRole = getUserRole();
    return roles.includes(userRole) || roles.includes(UserRole.ALL);
  };
  
  /**
   * Check if the current user has all of the specified permissions
   */
  const hasAllPermissions = (permissions: Permission[]): boolean => {
    return permissions.every(permission => hasPermission(permission));
  };
  
  /**
   * Check if the current user has any of the specified permissions
   */
  const hasAnyPermission = (permissions: Permission[]): boolean => {
    return permissions.some(permission => hasPermission(permission));
  };
  
  /**
   * Check if the current user can view a specific module
   */
  const canViewModule = (module: string): boolean => {
    const viewPermissions: Record<string, Permission> = {
      'frameworks': Permission.FRAMEWORK_VIEW,
      'obligations': Permission.OBLIGATION_VIEW,
      'controls': Permission.CONTROL_VIEW,
      'requirements': Permission.REQUIREMENT_VIEW,
      'tasks': Permission.TASK_VIEW,
      'documents': Permission.DOCUMENT_VIEW,
      'users': Permission.USER_VIEW,
      'settings': Permission.SETTINGS_VIEW,
      'reports': Permission.REPORT_VIEW,
      'audit-trail': Permission.AUDIT_VIEW,
      'analytics': Permission.ANALYTICS_VIEW,
      'executive-dashboard': Permission.EXECUTIVE_DASHBOARD,
      'team': Permission.TEAM_VIEW
    };
    
    const permission = viewPermissions[module];
    return permission ? hasPermission(permission) : false;
  };
  
  /**
   * Check if the current user can create in a specific module
   */
  const canCreate = (module: string): boolean => {
    const createPermissions: Record<string, Permission> = {
      'frameworks': Permission.FRAMEWORK_CREATE,
      'obligations': Permission.OBLIGATION_CREATE,
      'controls': Permission.CONTROL_CREATE,
      'requirements': Permission.REQUIREMENT_CREATE,
      'tasks': Permission.TASK_CREATE,
      'documents': Permission.DOCUMENT_CREATE,
      'users': Permission.USER_CREATE,
      'reports': Permission.REPORT_CREATE
    };
    
    const permission = createPermissions[module];
    return permission ? hasPermission(permission) : false;
  };
  
  /**
   * Check if the current user can edit in a specific module
   */
  const canEdit = (module: string, isOwn: boolean = false): boolean => {
    const editPermissions: Record<string, Permission> = {
      'frameworks': Permission.FRAMEWORK_EDIT,
      'obligations': Permission.OBLIGATION_EDIT,
      'controls': Permission.CONTROL_EDIT,
      'requirements': Permission.REQUIREMENT_EDIT,
      'tasks': isOwn ? Permission.TASK_EDIT_OWN : Permission.TASK_EDIT,
      'documents': Permission.DOCUMENT_EDIT,
      'users': Permission.USER_EDIT,
      'settings': Permission.SETTINGS_EDIT
    };
    
    const permission = editPermissions[module];
    return permission ? hasPermission(permission) : false;
  };
  
  /**
   * Check if the current user can delete in a specific module
   */
  const canDelete = (module: string): boolean => {
    const deletePermissions: Record<string, Permission> = {
      'frameworks': Permission.FRAMEWORK_DELETE,
      'obligations': Permission.OBLIGATION_DELETE,
      'controls': Permission.CONTROL_DELETE,
      'requirements': Permission.REQUIREMENT_DELETE,
      'tasks': Permission.TASK_DELETE,
      'documents': Permission.DOCUMENT_DELETE,
      'users': Permission.USER_DELETE
    };
    
    const permission = deletePermissions[module];
    return permission ? hasPermission(permission) : false;
  };
  
  return {
    user,
    userRole: getUserRole(),
    hasPermission,
    hasAnyRole,
    hasAllPermissions,
    hasAnyPermission,
    canViewModule,
    canCreate,
    canEdit,
    canDelete
  };
};
